// iOS Modal Scrolling Utility
// Handles body scroll prevention and iOS-specific modal behaviors

class ModalUtils {
  constructor() {
    this.openModals = new Set();
    this.originalBodyStyle = {};
    this.isIOS = this.detectIOS();
    this.viewportHeight = window.innerHeight;

    // Listen for viewport changes (iOS Safari address bar)
    if (this.isIOS) {
      window.addEventListener("resize", this.handleViewportChange.bind(this));
      window.addEventListener(
        "orientationchange",
        this.handleViewportChange.bind(this),
      );
    }
  }

  // Detect iOS devices
  detectIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ||
      (window.navigator.userAgent.includes("Safari") &&
        window.navigator.userAgent.includes("Mobile"))
    );
  }

  // Handle viewport changes on iOS
  handleViewportChange() {
    // Debounce viewport changes
    clearTimeout(this.viewportTimeout);
    this.viewportTimeout = setTimeout(() => {
      const newHeight = window.innerHeight;
      if (Math.abs(newHeight - this.viewportHeight) > 100) {
        this.viewportHeight = newHeight;
        this.updateModalHeights();
      }
    }, 150);
  }

  // Update modal heights after viewport change
  updateModalHeights() {
    const modals = document.querySelectorAll(".modal-dialog");
    modals.forEach((modal) => {
      if (this.isIOS) {
        modal.style.maxHeight = `calc(${this.viewportHeight}px - 2rem)`;
      }
    });
  }

  // Open modal with iOS-specific handling
  openModal(modalId) {
    this.openModals.add(modalId);

    if (this.openModals.size === 1) {
      this.preventBodyScroll();
    }

    // iOS-specific modal setup
    if (this.isIOS) {
      setTimeout(() => {
        this.setupIOSModal(modalId);
      }, 0);
    }
  }

  // Close modal with cleanup
  closeModal(modalId) {
    this.openModals.delete(modalId);

    if (this.openModals.size === 0) {
      this.restoreBodyScroll();
    }

    // iOS-specific cleanup
    if (this.isIOS) {
      this.cleanupIOSModal(modalId);
    }
  }

  // Prevent body scrolling when modal is open
  preventBodyScroll() {
    const body = document.body;

    // Store original styles
    this.originalBodyStyle = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      height: body.style.height,
    };

    // Get current scroll position
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Apply modal-open class and styles
    body.classList.add("modal-open");

    if (this.isIOS) {
      // iOS-specific body locking
      body.style.position = "fixed";
      body.style.top = `-${scrollTop}px`;
      body.style.left = `-${scrollLeft}px`;
      body.style.width = "100%";
      body.style.height = "100%";
      body.style.overflow = "hidden";

      // Store scroll position for restoration
      this.scrollPosition = { top: scrollTop, left: scrollLeft };
    } else {
      // Standard body locking
      body.style.overflow = "hidden";
    }
  }

  // Restore body scrolling when all modals are closed
  restoreBodyScroll() {
    const body = document.body;

    // Remove modal-open class
    body.classList.remove("modal-open");

    // Restore original styles
    Object.keys(this.originalBodyStyle).forEach((key) => {
      if (this.originalBodyStyle[key] !== undefined) {
        body.style[key] = this.originalBodyStyle[key];
      } else {
        body.style.removeProperty(key);
      }
    });

    // Restore scroll position on iOS
    if (this.isIOS && this.scrollPosition) {
      window.scrollTo(this.scrollPosition.left, this.scrollPosition.top);
      this.scrollPosition = null;
    }
  }

  // Setup iOS-specific modal behavior
  setupIOSModal(modalId) {
    const modal =
      document.querySelector(`[data-modal-id="${modalId}"]`) ||
      document.querySelector(".modal-overlay");

    if (!modal) return;

    // Set up proper heights for iOS
    const modalDialog = modal.querySelector(".modal-dialog");
    const modalBody = modal.querySelector(".modal-body");

    if (modalDialog) {
      modalDialog.style.maxHeight = `calc(${this.viewportHeight}px - 2rem)`;
    }

    // Enable momentum scrolling for modal content
    if (modalBody) {
      modalBody.style.webkitOverflowScrolling = "touch";
      modalBody.style.overflowY = "auto";
    }

    // Handle chat messages container specifically
    const chatMessages = modal.querySelector(".chat-messages");
    if (chatMessages) {
      chatMessages.style.webkitOverflowScrolling = "touch";
      chatMessages.style.overflowY = "auto";
      chatMessages.style.maxHeight = `calc(${this.viewportHeight}px - 200px)`;
    }

    // Handle user select wrappers
    const userSelectWrappers = modal.querySelectorAll(".user-select-wrapper");
    userSelectWrappers.forEach((wrapper) => {
      wrapper.style.webkitOverflowScrolling = "touch";
      wrapper.style.overflowY = "auto";
    });

    // Prevent iOS scroll bounce on modal overlay
    modal.addEventListener("touchmove", this.handleTouchMove.bind(this), {
      passive: false,
    });
  }

  // Cleanup iOS-specific modal behavior
  cleanupIOSModal(modalId) {
    const modal =
      document.querySelector(`[data-modal-id="${modalId}"]`) ||
      document.querySelector(".modal-overlay");

    if (!modal) return;

    // Remove touch move listeners
    modal.removeEventListener("touchmove", this.handleTouchMove.bind(this));
  }

  // Handle touch move to prevent scroll bounce
  handleTouchMove(e) {
    const target = e.target;
    const scrollableParent = this.findScrollableParent(target);

    if (!scrollableParent) {
      // No scrollable parent found, prevent default to stop bounce
      e.preventDefault();
      return;
    }

    // Check if we're at the scroll boundaries
    const isAtTop = scrollableParent.scrollTop === 0;
    const isAtBottom =
      scrollableParent.scrollTop + scrollableParent.clientHeight >=
      scrollableParent.scrollHeight;

    // Get touch direction
    const touchY = e.touches[0].clientY;
    const deltaY = touchY - (this.lastTouchY || touchY);
    this.lastTouchY = touchY;

    // Prevent bounce at boundaries
    if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
      e.preventDefault();
    }
  }

  // Find the nearest scrollable parent
  findScrollableParent(element) {
    let parent = element;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;

      if (overflowY === "auto" || overflowY === "scroll") {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  // Utility method to check if we're on iOS
  isIOSDevice() {
    return this.isIOS;
  }

  // Get current viewport height (useful for dynamic height calculations)
  getViewportHeight() {
    return this.viewportHeight;
  }
}

// Create singleton instance
const modalUtils = new ModalUtils();

export default modalUtils;

// Vue 3 compatible plugin
export const ModalUtilsPlugin = {
  install(app) {
    app.config.globalProperties.$modalUtils = modalUtils;
    app.provide("modalUtils", modalUtils);
  },
};

// Composable for Vue 3 Composition API
export function useModalUtils() {
  return {
    modalUtils,
    openModal: (modalId) => modalUtils.openModal(modalId),
    closeModal: (modalId) => modalUtils.closeModal(modalId),
    isIOS: modalUtils.isIOSDevice(),
    viewportHeight: modalUtils.getViewportHeight(),
  };
}
